/**
 * SB2-W-16 — small fetch helper for the `projects/v1` namespace.
 *
 * Mirrors `assetsFetch.ts` / `notificationsFetch.ts` — base URL comes
 * from `getProjectsBaseUrl()` so `DEV_API_MODE` (realbe / devapi-*)
 * routes requests correctly. Errors carry the parsed JSON body so the
 * UI can surface BE error envelopes (`code`, `reason`).
 */
import { getProjectsBaseUrl, useAuthStore } from "@aeriosiq/auth-client";

export interface ProjectsFetchError extends Error {
  status: number;
  body: unknown;
}

interface ProjectsFetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
}

function authHeaders(): Headers {
  const session = useAuthStore.getState().session;
  const tenant = useAuthStore.getState().tenant;
  const headers = new Headers();
  if (session?.accessToken) {
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  }
  if (tenant?.tenantSlug) {
    headers.set("X-Tenant-ID", tenant.tenantSlug);
  }
  return headers;
}

export async function projectsFetch<T>(
  path: string,
  opts: ProjectsFetchOptions = {},
): Promise<T> {
  const headers = authHeaders();
  let bodyInit: BodyInit | undefined;
  if (opts.body !== undefined) {
    bodyInit = JSON.stringify(opts.body);
    headers.set("Content-Type", "application/json");
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (bodyInit !== undefined) {
    init.body = bodyInit;
  }
  const res = await fetch(`${getProjectsBaseUrl()}${path}`, init);
  if (!res.ok) {
    let parsedBody: unknown = null;
    try {
      parsedBody = await res.json();
    } catch {
      // Non-JSON error body — status still surfaces.
    }
    const err = new Error(
      `projects fetch failed: ${path} → ${String(res.status)}`,
    ) as ProjectsFetchError;
    err.status = res.status;
    err.body = parsedBody;
    throw err;
  }
  if (res.status === 204) {
    return undefined as unknown as T;
  }
  const text = await res.text();
  if (text === "") return undefined as unknown as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined as unknown as T;
  }
}
